import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  CardHeader,
  IconButton,
  Divider,
  FormGroup,
  FormControlLabel,
  Switch,
  Alert,
  Chip,
  InputAdornment,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material';
import {
  Add,
  Delete,
  Save,
  Cancel,
  Info,
  DragIndicator,
  Edit,
  Close
} from '@mui/icons-material';
import ImageUpload from '../../components/common/ImageUpload';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../../context/AppContext';
import axios from 'axios';
import { appConfig } from '../../config/app';

const AddProduct = () => {
  const navigate = useNavigate();
  const { actions } = useApp();
  
  // Product basic info
  const [productData, setProductData] = useState({
    name: '',
    category: '',
    subCategory: '',
    description: '',
    price: '',
    livePrice: '',
    grossWeight: '',
    netWeight: '',
    quantity: '',
    image: '',
    images: [],
    metadata: {
      age: '',
      aka: '',
      metaTitle: '',
      metaDescription: '',
      metaKeywords: '',
      shelfLife: '',
      style: ''
    },
    status: 'active',
    sortOrder: 1
  });

  // Variants
  const [variants, setVariants] = useState([]);

  // Customizations
  const [customizations, setCustomizations] = useState([]);

  // UI state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [categories, setCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [filteredSubCategories, setFilteredSubCategories] = useState([]);

  // Modal states
  const [variantModalOpen, setVariantModalOpen] = useState(false);
  const [customizationModalOpen, setCustomizationModalOpen] = useState(false);
  const [optionModalOpen, setOptionModalOpen] = useState(false);
  const [editingVariantIndex, setEditingVariantIndex] = useState(null);
  const [editingCustomizationIndex, setEditingCustomizationIndex] = useState(null);
  const [activeCustomizationIndex, setActiveCustomizationIndex] = useState(null);

  // Form states for modals
  const [variantForm, setVariantForm] = useState({
    weight: '',
    weightValue: 0,
    sku: '',
    price: '',
    focusPrice: 0,
    sortOrder: 1,
    isActive: true,
    visibleToCustomer: true
  });

  // Helper: compute focusPrice for display (price/weight per KG)
  const getFocusPrice = (v) => {
    let wv = parseFloat(v?.weightValue);
    if (!wv || wv <= 0) {
      const m = String(v?.weight || '').match(/(\d+(?:\.\d+)?)/);
      wv = m ? parseFloat(m[1]) : 0;
    }
    const pr = parseFloat(v?.price) || 0;
    return wv > 0 ? (pr / wv).toFixed(2) : '-';
  };

  const [customizationForm, setCustomizationForm] = useState({
    displayName: '',
    description: '',
    uiType: 'radio',
    required: false,
    enabled: true,
    sortOrder: 1,
    options: []
  });

  const [optionForm, setOptionForm] = useState({
    name: '',
    sku: '',
    additionalPrice: '',
    sortOrder: 1,
    isDefault: false
  });

  // Predefined categories - replaced with dynamic categories
  // const categories = ['chicken', 'eggs', 'mutton'];

  // Fetch categories from API
  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${appConfig.api.baseURL}/categories`);
      if (response.data.success) {
        const categoryNames = response.data.data.map(cat => cat.name.toLowerCase());
        setCategories(response.data.data); // Store full category objects
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
      // Fallback to hardcoded categories
      setCategories([
        { id: 'chicken', name: 'chicken' },
        { id: 'eggs', name: 'eggs' },
        { id: 'mutton', name: 'mutton' }
      ]);
    }
  };

  // Fetch sub-categories from API
  const fetchSubCategories = async () => {
    try {
      const response = await axios.get(`${appConfig.api.baseURL}/categories/sub-categories/list`);
      if (response.data.success) {
        setSubCategories(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching sub-categories:', error);
      setSubCategories([]);
    }
  };

  // Load categories and subcategories on component mount
  useEffect(() => {
    fetchCategories();
    fetchSubCategories();
  }, []);

  // Filter subcategories when category changes
  useEffect(() => {
    if (productData.category && subCategories.length > 0) {
      // Filter subcategories that belong to the selected category
      const filtered = subCategories.filter(subCat => 
        subCat.parentCategoryId === productData.category
      );
      setFilteredSubCategories(filtered);
    } else {
      setFilteredSubCategories([]);
    }
    
    // Reset subcategory when category changes
    if (productData.subCategory) {
      setProductData(prev => ({ ...prev, subCategory: '' }));
    }
  }, [productData.category, subCategories]);

  // Predefined customization templates - removed as we'll build them dynamically
  // const customizationTemplates = { ... };

  // Handle basic info change
  const handleProductDataChange = (field, value) => {
    setProductData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Variant Modal Functions
  const openVariantModal = (index = null) => {
    if (index !== null) {
      setVariantForm(variants[index]);
      setEditingVariantIndex(index);
    } else {
      setVariantForm({
        weight: '',
        weightValue: 0,
        sku: '',
        price: '',
        focusPrice: 0,
        sortOrder: variants.length + 1,
        isActive: true,
        visibleToCustomer: true
      });
      setEditingVariantIndex(null);
    }
    setVariantModalOpen(true);
  };

  const closeVariantModal = () => {
    setVariantModalOpen(false);
    setEditingVariantIndex(null);
    setVariantForm({
      weight: '',
      weightValue: 0,
      sku: '',
      price: '',
      focusPrice: 0,
      sortOrder: variants.length + 1,
      isActive: true,
      visibleToCustomer: true
    });
  };

  const handleVariantFormChange = (field, value) => {
    setVariantForm(prev => {
      const updated = { ...prev, [field]: value };
      
      // Auto-calculate weightValue for sorting
      if (field === 'weight') {
        const weightMatch = value.match(/(\d+(?:\.\d+)?)/);
        if (weightMatch) {
          updated.weightValue = parseFloat(weightMatch[1]);
        }
      }
      
      // Auto-calculate focusPrice (price/weight per KG)
      let wv = parseFloat(updated.weightValue);
      if (!wv || wv <= 0) {
        const m = String(updated.weight || '').match(/(\d+(?:\.\d+)?)/);
        wv = m ? parseFloat(m[1]) : 0;
      }
      const pr = parseFloat(updated.price) || 0;
      updated.focusPrice = wv > 0 ? pr / wv : 0;
      
      return updated;
    });
  };

  const submitVariant = () => {
    if (!variantForm.weight.trim()) {
      setError('Weight/Size is required');
      return;
    }
    if (!variantForm.sku.trim()) {
      setError('SKU ID is required');
      return;
    }
    if (!variantForm.price || parseFloat(variantForm.price) <= 0) {
      setError('Price must be greater than 0');
      return;
    }

    // Check for duplicate SKU
    const isDuplicateSKU = variants.some((variant, index) => 
      variant.sku.toLowerCase() === variantForm.sku.toLowerCase() && 
      index !== editingVariantIndex
    );
    
    if (isDuplicateSKU) {
      setError('SKU ID already exists. Please use a unique SKU.');
      return;
    }

    if (editingVariantIndex !== null) {
      // Edit existing variant
      setVariants(prev => prev.map((variant, index) => 
        index === editingVariantIndex ? variantForm : variant
      ));
    } else {
      // Add new variant
      setVariants(prev => [...prev, variantForm]);
    }
    
    closeVariantModal();
    setError('');
  };

  const removeVariant = (index) => {
    setVariants(prev => prev.filter((_, i) => i !== index));
  };

  // Customization Modal Functions
  const openCustomizationModal = (index = null) => {
    if (index !== null) {
      setCustomizationForm(customizations[index]);
      setEditingCustomizationIndex(index);
    } else {
      setCustomizationForm({
        displayName: '',
        description: '',
        uiType: 'radio',
        required: false,
        enabled: true,
        sortOrder: customizations.length + 1,
        options: []
      });
      setEditingCustomizationIndex(null);
    }
    setCustomizationModalOpen(true);
  };

  const closeCustomizationModal = () => {
    setCustomizationModalOpen(false);
    setEditingCustomizationIndex(null);
    setCustomizationForm({
      displayName: '',
      description: '',
      uiType: 'radio',
      required: false,
      enabled: true,
      sortOrder: customizations.length + 1,
      options: []
    });
  };

  const handleCustomizationFormChange = (field, value) => {
    setCustomizationForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const submitCustomization = () => {
    if (!customizationForm.displayName.trim()) {
      setError('Customization name is required');
      return;
    }

    const customizationWithId = {
      ...customizationForm,
      id: editingCustomizationIndex !== null 
        ? customizations[editingCustomizationIndex].id 
        : `custom_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
    };

    if (editingCustomizationIndex !== null) {
      // Edit existing customization
      setCustomizations(prev => prev.map((customization, index) => 
        index === editingCustomizationIndex ? customizationWithId : customization
      ));
    } else {
      // Add new customization
      setCustomizations(prev => [...prev, customizationWithId]);
    }
    
    closeCustomizationModal();
    setError('');
  };

  const removeCustomization = (index) => {
    setCustomizations(prev => prev.filter((_, i) => i !== index));
  };

  // Option Modal Functions
  const openOptionModal = (customizationIndex, optionIndex = null) => {
    setActiveCustomizationIndex(customizationIndex);
    
    if (optionIndex !== null) {
      setOptionForm(customizations[customizationIndex].options[optionIndex]);
    } else {
      setOptionForm({
        name: '',
        sku: '',
        additionalPrice: '',
        sortOrder: customizations[customizationIndex].options.length + 1,
        isDefault: false
      });
    }
    setOptionModalOpen(true);
  };

  const closeOptionModal = () => {
    setOptionModalOpen(false);
    setActiveCustomizationIndex(null);
    setOptionForm({
      name: '',
      sku: '',
      additionalPrice: '',
      sortOrder: 1,
      isDefault: false
    });
  };

  const submitOption = () => {
    if (!optionForm.name.trim()) {
      setError('Option name is required');
      return;
    }

    // Only check for duplicate SKU if SKU is provided
    if (optionForm.sku && optionForm.sku.trim()) {
      // Check for duplicate SKU within the same customization
      const isDuplicateSKU = customizations[activeCustomizationIndex].options.some(option => 
        option.sku && option.sku.toLowerCase() === optionForm.sku.toLowerCase()
      );
      
      if (isDuplicateSKU) {
        setError('SKU ID already exists in this customization. Please use a unique SKU.');
        return;
      }

      // Check for duplicate SKU across all customizations
      const isDuplicateGlobalSKU = customizations.some((customization, custIndex) => 
        customization.options.some(option => 
          option.sku && option.sku.toLowerCase() === optionForm.sku.toLowerCase() && custIndex !== activeCustomizationIndex
        )
      );

      if (isDuplicateGlobalSKU) {
        setError('SKU ID already exists in another customization. Please use a unique SKU.');
        return;
      }
    }

    const optionWithId = {
      ...optionForm,
      id: `opt_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
    };

    setCustomizations(prev => prev.map((customization, index) => {
      if (index === activeCustomizationIndex) {
        return {
          ...customization,
          options: [...customization.options, optionWithId]
        };
      }
      return customization;
    }));

    closeOptionModal();
    setError('');
  };

  const removeOption = (customizationIndex, optionIndex) => {
    setCustomizations(prev => prev.map((customization, index) => {
      if (index === customizationIndex) {
        return {
          ...customization,
          options: customization.options.filter((_, i) => i !== optionIndex)
        };
      }
      return customization;
    }));
  };

  // Validate form
  const validateForm = () => {
    if (!productData.name.trim()) {
      setError('Product name is required');
      return false;
    }
    if (!productData.category) {
      setError('Category is required');
      return false;
    }
    if (variants.length === 0) {
      setError('At least one variant is required');
      return false;
    }
    for (let i = 0; i < variants.length; i++) {
      if (!variants[i].weight.trim()) {
        setError(`Variant ${i + 1}: Weight is required`);
        return false;
      }
      if (!variants[i].sku.trim()) {
        setError(`Variant ${i + 1}: SKU ID is required`);
        return false;
      }
      if (variants[i].price <= 0) {
        setError(`Variant ${i + 1}: Price must be greater than 0`);
        return false;
      }
    }
    
    // Validate customizations
    for (let i = 0; i < customizations.length; i++) {
      const customization = customizations[i];
      if (!customization.displayName.trim()) {
        setError(`Customization ${i + 1}: Name is required`);
        return false;
      }
      if (customization.options.length === 0) {
        setError(`Customization ${i + 1}: At least one option is required`);
        return false;
      }
      for (let j = 0; j < customization.options.length; j++) {
        if (!customization.options[j].name.trim()) {
          setError(`Customization ${i + 1}, Option ${j + 1}: Name is required`);
          return false;
        }
      }
    }
    
    return true;
  };

  // Handle form submission
  const handleSubmit = async () => {
    if (!validateForm()) return;

    try {
      setLoading(true);
      setError('');

      // Convert customizations array to object format for storage
      const customizationsObj = {};
      customizations.forEach((customization, index) => {
        const key = customization.displayName.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');
        customizationsObj[key] = {
          enabled: customization.enabled,
          displayName: customization.displayName,
          description: customization.description || '',
          sortOrder: customization.sortOrder,
          required: customization.required,
          uiType: customization.uiType,
          options: customization.options.map(option => ({
            id: option.id,
            name: option.name,
            sku: option.sku, // Include sku in the object
            additionalPrice: option.additionalPrice,
            sortOrder: option.sortOrder,
            isDefault: option.isDefault
          }))
        };
      });

      // Convert category and sub-category documentIds to names for backend
      const selectedCategory = categories.find(cat => cat.documentId === productData.category);
      const selectedSubCategory = filteredSubCategories.find(subCat => subCat.documentId === productData.subCategory);
      
      const submitData = {
        ...productData,
        category: selectedCategory ? selectedCategory.name : productData.category,
        subCategory: selectedSubCategory ? selectedSubCategory.name : (productData.subCategory || null),
        variants,
        customizations: customizationsObj
      };

      const response = await axios.post(`${appConfig.api.baseURL}/products`, submitData);

      if (response.data.success) {
        actions.showSnackbar('Product created successfully', 'success');
        navigate('/super-admin/products/list');
      } else {
        setError(response.data.message || 'Failed to create product');
      }
    } catch (error) {
      console.error('Error creating product:', error);
      setError('Failed to create product. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Add New Product
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Create a new product with variants and customization options
        </Typography>
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Basic Information */}
      <Paper elevation={2} sx={{ mb: 4 }}>
        <Box sx={{ p: 3, borderBottom: 1, borderColor: 'divider' }}>
          <Typography variant="h6" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
            <Info />
            Basic Information
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Enter the core details of your product
          </Typography>
        </Box>
        
        <Box sx={{ p: 4 }}>
          <Stack spacing={3}>
            <TextField
              fullWidth
              label="Product Name"
              value={productData.name}
              onChange={(e) => handleProductDataChange('name', e.target.value)}
              required
              placeholder="e.g., Fresh Chicken Breast"
              variant="outlined"
            />
            
            <FormControl fullWidth required>
              <InputLabel>Category</InputLabel>
              <Select
                value={productData.category}
                onChange={(e) => handleProductDataChange('category', e.target.value)}
                label="Category"
              >
                {categories.map((category) => (
                  <MenuItem key={category.documentId} value={category.documentId}>
                    {category.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>Sub Category</InputLabel>
              <Select
                value={productData.subCategory}
                onChange={(e) => handleProductDataChange('subCategory', e.target.value)}
                label="Sub Category"
                disabled={!productData.category || filteredSubCategories.length === 0}
              >
                <MenuItem value="">
                  <em>No Sub Category</em>
                </MenuItem>
                {filteredSubCategories.map((subCategory) => (
                  <MenuItem key={subCategory.documentId} value={subCategory.documentId}>
                    {subCategory.name}
                  </MenuItem>
                ))}
              </Select>
              {productData.category && filteredSubCategories.length === 0 && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, ml: 1.5 }}>
                  No sub-categories available for {categories.find(cat => cat.documentId === productData.category)?.name || productData.category}
                </Typography>
              )}
            </FormControl>

            <TextField
              fullWidth
              label="Product Description"
              value={productData.description}
              onChange={(e) => handleProductDataChange('description', e.target.value)}
              multiline
              rows={4}
              placeholder="Provide a detailed description of your product, including quality, sourcing, and preparation details..."
              variant="outlined"
            />

            <TextField
              fullWidth
              label="Base Price"
              type="number"
              value={productData.price}
              onChange={(e) => handleProductDataChange('price', e.target.value)}
              InputProps={{
                startAdornment: <InputAdornment position="start">₹</InputAdornment>
              }}
              placeholder="0"
              helperText="Base price for this product (can be overridden by variant prices)"
              variant="outlined"
            />

            <TextField
              fullWidth
              label="Live Price"
              type="number"
              value={productData.livePrice}
              onChange={(e) => handleProductDataChange('livePrice', e.target.value)}
              InputProps={{
                startAdornment: <InputAdornment position="start">₹</InputAdornment>
              }}
              placeholder="0"
              helperText="Current live price for this product"
              variant="outlined"
            />

            <TextField
              fullWidth
              label="Gross Weight"
              value={productData.grossWeight}
              onChange={(e) => handleProductDataChange('grossWeight', e.target.value)}
              placeholder="e.g., 1.5kg, 500g"
              helperText="Gross weight including packaging"
              variant="outlined"
            />

            <TextField
              fullWidth
              label="Net Weight"
              value={productData.netWeight}
              onChange={(e) => handleProductDataChange('netWeight', e.target.value)}
              placeholder="e.g., 1.2kg, 450g"
              helperText="Net weight of the product only"
              variant="outlined"
            />

            <TextField
              fullWidth
              label="Quantity"
              value={productData.quantity}
              onChange={(e) => handleProductDataChange('quantity', e.target.value)}
              placeholder="e.g., 1kg, 500g, 6 pieces, 1 dozen"
              helperText="Default quantity or unit description for this product"
              variant="outlined"
            />

            <FormControl fullWidth>
              <InputLabel>Product Status</InputLabel>
              <Select
                value={productData.status}
                onChange={(e) => handleProductDataChange('status', e.target.value)}
                label="Product Status"
              >
                <MenuItem value="active">
                  <Chip label="Active" color="success" size="small" sx={{ mr: 1 }} />
                  Active
                </MenuItem>
                <MenuItem value="inactive">
                  <Chip label="Inactive" color="default" size="small" sx={{ mr: 1 }} />
                  Inactive
                </MenuItem>
              </Select>
            </FormControl>

            <Box>
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                Product Images
              </Typography>
              <ImageUpload
                images={productData.images}
                onImagesChange={(newImages) => {
                  setProductData(prev => ({
                    ...prev,
                    images: newImages,
                    image: newImages[0] || '' // Set first image as main image
                  }));
                }}
                uploadPath="products"
                maxImages={5}
                label="Upload Product Images"
                helperText="Upload high-quality images for your product. First image will be used as the main product image."
                multiple={true}
              />
            </Box>

          </Stack>
        </Box>
      </Paper>

      {/* Metadata */}
      <Paper elevation={2} sx={{ mb: 4 }}>
        <Box sx={{ p: 3, borderBottom: 1, borderColor: 'divider' }}>
          <Typography variant="h6" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
            <Info sx={{ mr: 1 }} />
            Metadata
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Additional product information for SEO and product identification
          </Typography>
        </Box>
        
        <Box sx={{ p: 4 }}>
          <Grid container spacing={2} width="100%">
            <Grid item xs={12} sm={6} width="45%">
              <TextField
                fullWidth
                label="Age"
                value={productData.metadata.age}
                onChange={(e) => handleProductDataChange('metadata', {
                  ...productData.metadata,
                  age: e.target.value
                })}
                placeholder="e.g., 3-6 months"
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12} sm={6}  width="45%">
              <TextField
                fullWidth
                label="AKA (Also Known As)"
                value={productData.metadata.aka}
                onChange={(e) => handleProductDataChange('metadata', {
                  ...productData.metadata,
                  aka: e.target.value
                })}
                placeholder="e.g., Chicken Breast, White Meat"
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12} sm={6}  width="45%">
              <TextField
                fullWidth
                label="Shelf Life"
                value={productData.metadata.shelfLife}
                onChange={(e) => handleProductDataChange('metadata', {
                  ...productData.metadata,
                  shelfLife: e.target.value
                })}
                placeholder="e.g., 3-5 days refrigerated"
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12} sm={6} width="45%">
              <TextField
                fullWidth
                label="Style"
                value={productData.metadata.style}
                onChange={(e) => handleProductDataChange('metadata', {
                  ...productData.metadata,
                  style: e.target.value
                })}
                placeholder="e.g., Fresh, Frozen, Organic"
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12} sm={6}  width="45%">
              <TextField
                fullWidth
                label="Meta Title"
                value={productData.metadata.metaTitle}
                onChange={(e) => handleProductDataChange('metadata', {
                  ...productData.metadata,
                  metaTitle: e.target.value
                })}
                placeholder="SEO title for the product"
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12} sm={6}  width="45%">
              <TextField
                fullWidth
                label="Meta Keywords"
                value={productData.metadata.metaKeywords}
                onChange={(e) => handleProductDataChange('metadata', {
                  ...productData.metadata,
                  metaKeywords: e.target.value
                })}
                placeholder="SEO keywords separated by commas"
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12} sm={6}  width="90%">
              <TextField
                fullWidth
                label="Meta Description"
                value={productData.metadata.metaDescription}
                onChange={(e) => handleProductDataChange('metadata', {
                  ...productData.metadata,
                  metaDescription: e.target.value
                })}
                multiline
                rows={3}
                placeholder="SEO description for the product"
                variant="outlined"
              />
            </Grid>
           
       
          </Grid>
        </Box>
      </Paper>

      {/* Variants */}
      <Paper elevation={2} sx={{ mb: 4 }}>
        <Box sx={{ p: 3, borderBottom: 1, borderColor: 'divider' }}>
          <Typography variant="h6" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
            <DragIndicator />
            Product Variants
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Define different sizes, weights, and pricing options for your product
          </Typography>
        </Box>
        
        <Box sx={{ p: 4 }}>
          {variants.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 6 }}>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No Variants Added Yet
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Add variants with different weights and prices
              </Typography>
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={() => openVariantModal()}
                size="large"
                sx={{ borderRadius: 3, px: 4 }}
              >
                Add Your First Variant
              </Button>
            </Box>
          ) : (
            <>
              <TableContainer component={Paper} variant="outlined">
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>#</TableCell>
                      <TableCell>Weight/Size</TableCell>
                      <TableCell>SKU ID</TableCell>
                      <TableCell>Price</TableCell>
                      <TableCell>Focus Price (/KG)</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Visible to Customer</TableCell>
                      <TableCell>Sort Order</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {variants.map((variant, index) => (
                      <TableRow key={index}>
                        <TableCell>{index + 1}</TableCell>
                        <TableCell>{variant.weight}</TableCell>
                        <TableCell>
                          <Chip 
                            label={variant.sku} 
                            size="small" 
                            color="primary" 
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>₹{variant.price}</TableCell>
                        <TableCell>{getFocusPrice(variant) === '-' ? '-' : `₹${getFocusPrice(variant)}/KG`}</TableCell>
                        <TableCell>
                          <Chip 
                            label={variant.isActive ? 'Active' : 'Inactive'} 
                            size="small" 
                            color={variant.isActive ? 'success' : 'default'}
                            variant={variant.isActive ? 'filled' : 'outlined'}
                          />
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={variant.visibleToCustomer ? 'Visible' : 'Hidden'} 
                            size="small" 
                            color={variant.visibleToCustomer ? 'primary' : 'default'}
                            variant={variant.visibleToCustomer ? 'filled' : 'outlined'}
                          />
                        </TableCell>
                        <TableCell>{variant.sortOrder}</TableCell>
                        <TableCell>
                          <IconButton 
                            size="small" 
                            onClick={() => openVariantModal(index)}
                            color="primary"
                          >
                            <Edit />
                          </IconButton>
                          <IconButton 
                            size="small" 
                            onClick={() => removeVariant(index)}
                            color="error"
                          >
                            <Delete />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              
              <Box sx={{ textAlign: 'center', pt: 3 }}>
                <Button
                  startIcon={<Add />}
                  onClick={() => openVariantModal()}
                  variant="outlined"
                  size="large"
                >
                  Add Another Variant
                </Button>
              </Box>
            </>
          )}
        </Box>
      </Paper>

      {/* Customizations */}
      <Paper elevation={2} sx={{ mb: 4 }}>
        <Box sx={{ p: 3, borderBottom: 1, borderColor: 'divider' }}>
          <Typography variant="h6" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
            <DragIndicator />
            Customization Options
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Define custom options like skin type, cut size, and processing preferences
          </Typography>
        </Box>
        
        <Box sx={{ p: 4 }}>
          {customizations.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 6 }}>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No Customizations Added Yet
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Add customization options to let customers personalize their orders
              </Typography>
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={() => openCustomizationModal()}
                size="large"
                sx={{ borderRadius: 3, px: 4 }}
              >
                Add Your First Customization
              </Button>
            </Box>
          ) : (
            <Stack spacing={3}>
              {customizations.map((customization, index) => (
                <Card key={index} elevation={1} sx={{ border: 1, borderColor: 'divider' }}>
                  <CardHeader
                    title={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                          {customization.displayName}
                        </Typography>
                        <Chip 
                          label={customization.uiType} 
                          size="small" 
                          color="primary" 
                          variant="outlined"
                        />
                        <Chip 
                          label={customization.required ? 'Required' : 'Optional'} 
                          size="small" 
                          color={customization.required ? 'error' : 'default'}
                          variant="outlined"
                        />
                      </Box>
                    }
                    action={
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <IconButton
                          color="primary"
                          onClick={() => openCustomizationModal(index)}
                        >
                          <Edit />
                        </IconButton>
                        <IconButton
                          color="error"
                          onClick={() => removeCustomization(index)}
                        >
                          <Delete />
                        </IconButton>
                      </Box>
                    }
                  />
                  
                  <CardContent>
                    {customization.options.length === 0 ? (
                      <Box sx={{ textAlign: 'center', py: 2 }}>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          No options added yet
                        </Typography>
                        <Button
                          startIcon={<Add />}
                          onClick={() => openOptionModal(index)}
                          size="small"
                          variant="contained"
                        >
                          Add Options
                        </Button>
                      </Box>
                    ) : (
                      <>
                        <TableContainer component={Paper} variant="outlined" size="small">
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell>Option Name</TableCell>
                                <TableCell>SKU ID</TableCell>
                                <TableCell>Extra Price</TableCell>
                                <TableCell>Default</TableCell>
                                <TableCell>Actions</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {customization.options.map((option, optionIndex) => (
                                <TableRow key={optionIndex}>
                                  <TableCell>{option.name}</TableCell>
                                  <TableCell>
                                    {option.sku ? (
                                      <Chip 
                                        label={option.sku} 
                                        size="small" 
                                        color="secondary" 
                                        variant="outlined"
                                      />
                                    ) : (
                                      <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                        No SKU
                                      </Typography>
                                    )}
                                  </TableCell>
                                  <TableCell>₹{option.additionalPrice}</TableCell>
                                  <TableCell>
                                    {option.isDefault ? (
                                      <Chip label="Yes" color="success" size="small" />
                                    ) : (
                                      <Chip label="No" color="default" size="small" />
                                    )}
                                  </TableCell>
                                  <TableCell>
                                    <IconButton 
                                      size="small" 
                                      onClick={() => removeOption(index, optionIndex)}
                                      color="error"
                                    >
                                      <Delete />
                                    </IconButton>
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                        
                        <Box sx={{ textAlign: 'center', pt: 2 }}>
                          <Button
                            startIcon={<Add />}
                            onClick={() => openOptionModal(index)}
                            size="small"
                            variant="outlined"
                          >
                            Add More Options
                          </Button>
                        </Box>
                      </>
                    )}
                  </CardContent>
                </Card>
              ))}
              
              <Box sx={{ textAlign: 'center', pt: 2 }}>
                <Button
                  variant="outlined"
                  startIcon={<Add />}
                  onClick={() => openCustomizationModal()}
                  size="large"
                >
                  Add Another Customization
                </Button>
              </Box>
            </Stack>
          )}
        </Box>
      </Paper>

      {/* Variant Modal */}
      <Dialog open={variantModalOpen} onClose={closeVariantModal} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingVariantIndex !== null ? 'Edit Variant' : 'Add New Variant'}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            <TextField
              fullWidth
              label="Weight/Size"
              value={variantForm.weight}
              onChange={(e) => handleVariantFormChange('weight', e.target.value)}
              placeholder="e.g., 0.5kg, 1kg, 6 pieces"
              required
              helperText="Specify the weight or quantity for this variant"
            />
            
            <TextField
              fullWidth
              label="SKU ID"
              value={variantForm.sku}
              onChange={(e) => handleVariantFormChange('sku', e.target.value.toUpperCase())}
              placeholder="e.g., CHK-500G, CHK-1KG"
              required
              helperText="Unique identifier for this variant (will be converted to uppercase)"
              inputProps={{
                style: { textTransform: 'uppercase' }
              }}
            />
            
            <TextField
              fullWidth
              label="Price"
              type="number"
              value={variantForm.price}
              onChange={(e) => handleVariantFormChange('price', e.target.value)}
              InputProps={{
                startAdornment: <InputAdornment position="start">₹</InputAdornment>
              }}
              required
              helperText="Set the selling price for this variant"
            />
            
            <TextField
              label="Sort Order"
              type="number"
              value={variantForm.sortOrder}
              onChange={(e) => handleVariantFormChange('sortOrder', parseInt(e.target.value) || 1)}
              helperText="Order in which this variant appears to customers"
              fullWidth
            />
            
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={variantForm.isActive}
                      onChange={(e) => handleVariantFormChange('isActive', e.target.checked)}
                      color="success"
                    />
                  }
                  label="Active"
                />
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, textAlign: 'center' }}>
                  {variantForm.isActive ? 'Available for purchase' : 'Hidden from customers'}
                </Typography>
              </Box>

              <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={variantForm.visibleToCustomer}
                      onChange={(e) => handleVariantFormChange('visibleToCustomer', e.target.checked)}
                      color="primary"
                    />
                  }
                  label="Visible to Customer"
                />
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, textAlign: 'center' }}>
                  {variantForm.visibleToCustomer ? 'Customers can see this' : 'Hidden from customer view'}
                </Typography>
              </Box>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeVariantModal}>Cancel</Button>
          <Button onClick={submitVariant} variant="contained">
            {editingVariantIndex !== null ? 'Update' : 'Add'} Variant
          </Button>
        </DialogActions>
      </Dialog>

      {/* Customization Modal */}
      <Dialog open={customizationModalOpen} onClose={closeCustomizationModal} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingCustomizationIndex !== null ? 'Edit Customization' : 'Add New Customization'}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            <TextField
              fullWidth
              label="Customization Name"
              value={customizationForm.displayName}
              onChange={(e) => handleCustomizationFormChange('displayName', e.target.value)}
              placeholder="e.g., Skin Type, Cut Size"
              required
            />
            
            <TextField
              fullWidth
              label="Description"
              value={customizationForm.description}
              onChange={(e) => handleCustomizationFormChange('description', e.target.value)}
              placeholder="e.g., Choose how you want your chicken prepared"
              multiline
              rows={3}
              helperText="Optional description to help customers understand this customization"
            />
            
            <FormControl fullWidth>
              <InputLabel>Display Type</InputLabel>
              <Select
                value={customizationForm.uiType}
                onChange={(e) => handleCustomizationFormChange('uiType', e.target.value)}
                label="Display Type"
              >
                <MenuItem value="radio">Radio Buttons (Single Choice)</MenuItem>
                <MenuItem value="checkbox">Checkboxes (Multiple Choice)</MenuItem>
                <MenuItem value="dropdown">Dropdown Menu</MenuItem>
              </Select>
            </FormControl>
            
            <Box sx={{ display: 'flex', gap: 2 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={customizationForm.enabled}
                    onChange={(e) => handleCustomizationFormChange('enabled', e.target.checked)}
                  />
                }
                label="Enabled"
              />
              
              <FormControlLabel
                control={
                  <Switch
                    checked={customizationForm.required}
                    onChange={(e) => handleCustomizationFormChange('required', e.target.checked)}
                  />
                }
                label="Required"
              />
            </Box>
            
            <TextField
              fullWidth
              label="Sort Order"
              type="number"
              value={customizationForm.sortOrder}
              onChange={(e) => handleCustomizationFormChange('sortOrder', parseInt(e.target.value) || 1)}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeCustomizationModal}>Cancel</Button>
          <Button onClick={submitCustomization} variant="contained">
            {editingCustomizationIndex !== null ? 'Update' : 'Add'} Customization
          </Button>
        </DialogActions>
      </Dialog>

      {/* Option Modal */}
      <Dialog open={optionModalOpen} onClose={closeOptionModal} maxWidth="sm" fullWidth>
        <DialogTitle>Add Option</DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            <TextField
              fullWidth
              label="Option Name"
              value={optionForm.name}
              onChange={(e) => setOptionForm(prev => ({ ...prev, name: e.target.value }))}
              placeholder="e.g., With Skin, Skinless"
              required
              helperText="Descriptive name for this option"
            />
            
            <TextField
              fullWidth
              label="Option SKU ID (Optional)"
              value={optionForm.sku}
              onChange={(e) => setOptionForm(prev => ({ ...prev, sku: e.target.value.toUpperCase() }))}
              placeholder="e.g., SKIN-WITH, SKIN-WITHOUT"
              helperText="Optional unique identifier for this option (will be converted to uppercase)"
              inputProps={{
                style: { textTransform: 'uppercase' }
              }}
            />
            
            <TextField
              fullWidth
              label="Additional Price"
              type="number"
              value={optionForm.additionalPrice}
              onChange={(e) => setOptionForm(prev => ({ ...prev, additionalPrice: e.target.value }))}
              InputProps={{
                startAdornment: <InputAdornment position="start">₹</InputAdornment>
              }}
              helperText="Extra cost for this option (0 if no additional charge)"
            />
            
            <TextField
              fullWidth
              label="Sort Order"
              type="number"
              value={optionForm.sortOrder}
              onChange={(e) => setOptionForm(prev => ({ ...prev, sortOrder: parseInt(e.target.value) || 1 }))}
              helperText="Order in which this option appears to customers"
            />
            
            <FormControlLabel
              control={
                <Switch
                  checked={optionForm.isDefault}
                  onChange={(e) => setOptionForm(prev => ({ ...prev, isDefault: e.target.checked }))}
                />
              }
              label="Set as Default Option"
              sx={{ mt: 1 }}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeOptionModal}>Cancel</Button>
          <Button onClick={submitOption} variant="contained">Add Option</Button>
        </DialogActions>
      </Dialog>

      {/* Action Buttons */}
      <Paper sx={{ p: 2, display: 'flex', gap: 2, justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          Complete all required fields to create the product
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<Cancel />}
            onClick={() => navigate('/super-admin/products/list')}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            startIcon={<Save />}
            onClick={handleSubmit}
            disabled={loading}
          >
            {loading ? 'Creating...' : 'Create Product'}
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};

export default AddProduct; 